import { ref } from 'vue'
import { defineStore } from 'pinia'
import { Profile } from '~/models/Profile'
import { Itinerary2 } from '~/models/Itinerary'
import { ProfileDestination, ProfileDestinationType } from '~/models/ProfileDestination'
import { Destination2 } from '~/models/Destination'

export const useProfileStore = defineStore('user', () => {
	const profile = ref<Profile>()
	const country = ref<Destination2>()

	const previousItineraries = ref<Itinerary2[]>([])
	const nextItineraries = ref<Itinerary2[]>([])
	const onGoingItineraries = ref<Itinerary2[]>([])

	const destinations = ref<ProfileDestination[]>([])

	const setProfile = (newProfile: Profile) => {
		profile.value = newProfile
	}

	const resetProfile = () => {
		profile.value = undefined
	}

	const setCountry = (newCountry: Destination2) => {
		country.value = newCountry
	}

	const resetCountry = () => {
		country.value = undefined
	}

	const resetAll = () => {
		resetProfile()
		resetPreviousItineraries()
		resetNextItineraries()
		resetOnGoingItineraries()
		resetDestinations()
		resetCountry()
	}

	const addPreviousItineraries = (itineraries: Itinerary2[]) => {
		previousItineraries.value = itineraries
	}

	const resetPreviousItineraries = () => {
		previousItineraries.value = []
	}

	const addNextItineraries = (itineraries: Itinerary2[]) => {
		nextItineraries.value = itineraries
	}

	const resetNextItineraries = () => {
		nextItineraries.value = []
	}

	const addOnGoingItineraries = (itineraries: Itinerary2[]) => {
		onGoingItineraries.value = itineraries
	}

	const resetOnGoingItineraries = () => {
		onGoingItineraries.value = []
	}

	const addDestinations = (newDestinations: ProfileDestination[]): void => {
		destinations.value = newDestinations
	}

	const addDestinationsByType = (
		newDestinations: ProfileDestination[],
		type: ProfileDestinationType
	): void => {
		const withoutType = destinations.value.filter((destination) => destination.type !== type)
		destinations.value = [...withoutType, ...newDestinations]
	}

	const getDestinations = (): ProfileDestination[] => {
		return destinations.value
	}

	const getDestinationsByType = (type: ProfileDestinationType): ProfileDestination[] => {
		return destinations.value.filter((destination) => destination.type === type)
	}

	const resetDestinations = (): void => {
		destinations.value = []
	}

	const resetDestinationsByType = (type: ProfileDestinationType): void => {
		destinations.value = destinations.value.filter((destination) => destination.type !== type)
	}

	return {
		profile,
		previousItineraries,
		nextItineraries,
		addPreviousItineraries,
		resetPreviousItineraries,
		addNextItineraries,
		resetNextItineraries,
		setProfile,
		addOnGoingItineraries,
		resetOnGoingItineraries,
		resetAll,
		country,
		setCountry,
		resetCountry,
		addDestinations,
		addDestinationsByType,
		getDestinations,
		getDestinationsByType,
		resetDestinations,
		resetDestinationsByType,
	}
})
